import {
	closeSessionRefreshRetryPrompt,
	confirmSessionRefreshRetry
} from "../prompts/confirmSessionRefreshRetry";

const silentRenewRetryIntervalMs = 300 * 1000;

export class SilentRenewRecovery {
	constructor(options) {
		this.$window = options.$window;
		this.$logger = options.$logger;
		this.renewAccessToken = options.renewAccessToken;
		this.isSigninRequiredError = options.isSigninRequiredError;
		this.retryIntervalMs = options.retryIntervalMs || silentRenewRetryIntervalMs;
		this.retryTimeout = null;
		this.retryPromise = null;
		this.retryError = null;
		this.focusHandler = null;
		this.prompt = null;
		this.isPromptRetrying = false;
	}

	handleError = (error) => {
		if(this.isSigninRequiredError(error)) {
			this.stop(false);
		}
		else {
			this.start(error);
		}

		return this.showPrompt(error);
	}

	recordRenewalSucceeded = () => {
		this.stop(!this.isPromptRetrying);
	}

	start = (error) => {
		this.retryError = error;
		this.scheduleRetry();
		this.addFocusHandler();
	}

	stop = (closePrompt = true) => {
		this.retryError = null;

		if(this.retryTimeout) {
			this.$window.clearTimeout(this.retryTimeout);
			this.retryTimeout = null;
		}

		if(this.focusHandler) {
			this.$window.removeEventListener("focus", this.focusHandler);
			this.focusHandler = null;
		}

		if(closePrompt) {
			closeSessionRefreshRetryPrompt();
		}
	}

	scheduleRetry = () => {
		if(this.retryTimeout) {
			this.$window.clearTimeout(this.retryTimeout);
		}

		this.retryTimeout = this.$window.setTimeout(() => {
			this.retryTimeout = null;
			this.retry("timer");
		}, this.retryIntervalMs);
	}

	addFocusHandler = () => {
		if(this.focusHandler) {
			return;
		}

		this.focusHandler = () => this.retry("focus");
		this.$window.addEventListener("focus", this.focusHandler);
	}

	retry = (source) => {
		if(!this.retryError) {
			return Promise.resolve(false);
		}

		if(this.retryPromise) {
			return this.retryPromise;
		}

		this.retryPromise = this.renewAccessToken()
			.then(() => true)
			.catch(error => {
				this.$logger.exception(error, `Session renewal retry failed after ${source}`);

				if(this.isSigninRequiredError(error)) {
					this.stop(false);
				}
				else {
					this.start(error);
				}

				return false;
			})
			.finally(() => {
				this.retryPromise = null;
			});

		return this.retryPromise;
	}

	showPrompt = async(error) => {
		if(this.prompt) {
			return this.prompt;
		}

		try	{
			this.prompt = confirmSessionRefreshRetry(
				() => this.retryFromPrompt(),
				{ willRetryAutomatically: !this.isSigninRequiredError(error) }
			)
				.finally(() => {
					this.prompt = null;
				});

			return await this.prompt;
		}catch(promptError) {
			this.$logger.exception(promptError, "An uncaught error occurred during session-retry");
			this.prompt = null;

			return false;
		}
	}

	retryFromPrompt = () => {
		this.isPromptRetrying = true;

		return this.renewAccessToken()
			.then(() => true)
			.catch(error => {
				this.$logger.exception(error, "Session renewal retry failed after prompt");

				if(this.isSigninRequiredError(error)) {
					this.stop(false);
				}
				else {
					this.start(error);
				}

				return false;
			})
			.finally(() => {
				this.isPromptRetrying = false;
			});
	}
}
